/* eslint-disable no-shadow */
/* eslint-disable react/jsx-props-no-spreading */
import { useState, useEffect, useCallback, useMemo } from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import map from 'lodash/map';
import get from 'lodash/get';
import { injectIntl } from 'react-intl';
import { withLDConsumer } from 'launchdarkly-react-client-sdk';
import NoIndex from 'components/global/NoIndex';
import PreAuthTopBar from 'components/global/PreAuthTopBar';
import {
  fetchDesignations,
  fetchLanguages,
  fetchAgeGroups,
  fetchInsuranceCompanies,
  fetchApproachToCares,
  fetchTherapeuticOrientations,
  fetchPronouns,
  fetchDiagnosisTypes,
  fetchSpecialities,
  fetchReligions,
  fetchGenderIdentities,
  fetchRaces,
  fetchStates,
} from '../../actions/misc';
import Panel from './Panel';
import Introduction from './pages/Introduction';
import PersonalDetails from './pages/PersonalDetails';
import LocationDetails from './pages/LocationDetails';
import ProfessionalDetails1 from './pages/ProfessionalDetails1';
import ProfessionalDetails2 from './pages/ProfessionalDetails2';
import ProfessionalDetails3 from './pages/ProfessionalDetails3';
import ProfessionalDetails4 from './pages/ProfessionalDetails4';
import PracticeDetails0 from './pages/PracticeDetails0';
import PracticeDetails1 from './pages/PracticeDetails1';
import PracticeDetails2 from './pages/PracticeDetails2';
import PracticeDetails3 from './pages/PracticeDetails3';
import PracticeDetails4 from './pages/PracticeDetails4';
import PracticeDetails5 from './pages/PracticeDetails5';
import MatchingPreferences from './pages/MatchingPreferences';
import ThankYou from './pages/ThankYou';
import TermsOfUse from './pages/TermsOfUse';
import TaxInformation from './pages/TaxInformation';
import { fetchUser } from '../../actions/user';
import LoadingPanel from '../global/LoadingPanel';
import { scrollToTop } from './scroll';
import { COUNTRY } from '../../environment';
// eslint-disable-next-line import/no-cycle
import ErrorDialog from './ErrorDialog';
import {
  INTRODUCTION,
  PERSONAL_DETAILS,
  LOCATION_DETAILS,
  PROFESSIONAL_DETAILS,
  PRACTICE_DETAILS,
  MATCHING_PREFERENCES,
  TERMS_OF_USE,
  TAX_INFORMATION,
  THANK_YOU,
} from './helpers';
import { signoutUser } from '../../actions/auths';

const PracticeOnboarding = ({
  authenticated = false,
  ageGroups = [],
  designations = [],
  languages = [],
  insuranceCompanies = [],
  history,
  intl,
  // Action creators
  fetchAgeGroups,
  fetchDesignations,
  fetchLanguages,
  fetchInsuranceCompanies,
  fetchApproachToCares,
  fetchTherapeuticOrientations,
  fetchPronouns,
  fetchDiagnosisTypes,
  fetchSpecialities,
  fetchReligions,
  fetchGenderIdentities,
  fetchRaces,
  fetchStates,
  fetchUser,
  signoutUser,
}) => {
  const [page, setPage] = useState(INTRODUCTION);
  const [subPage, setSubPage] = useState(0);
  const [isClinicCounsellor, setIsClinicCounsellor] = useState(false);
  const [formDrafts, setFormDrafts] = useState({});
  const [loadingPanel, setLoadingPanel] = useState({
    open: false,
    message: '',
  });
  const [error, setError] = useState({
    open: false,
    message: '',
  });

  const determineRedirectPage = useCallback(user => {
    const avatar = get(user, 'avatar', false);
    const providerBio = get(user, 'provider_bio.overview', false);

    if (!avatar || !providerBio) return MATCHING_PREFERENCES;

    return MATCHING_PREFERENCES;
  }, []);

  const openErrorDialog = useCallback(message => {
    setError({ open: true, message });
  }, []);

  const closeErrorDialog = useCallback(() => {
    setError({ open: false, message: '' });
  }, []);

  const openLoadingPanel = useCallback(message => {
    scrollToTop();
    setLoadingPanel({ open: true, message });
  }, []);

  const closeLoadingPanel = useCallback(() => {
    setLoadingPanel({ open: false, message: '' });
  }, []);

  const errorCallback = useCallback(
    (error, message) => {
      closeLoadingPanel();
      openErrorDialog(message);
    },
    [closeLoadingPanel, openErrorDialog],
  );

  const redirect = useCallback(() => {
    openLoadingPanel(intl.formatMessage({ defaultMessage: 'Loading...' }));
    fetchUser(
      response => {
        const user = response.data;
        const urlParams = new URL(window.location.href).searchParams;

        closeLoadingPanel();
        if (user && urlParams.get('verify')) {
          setPage(determineRedirectPage(user));
        } else {
          setPage(LOCATION_DETAILS);
        }
      },
      () => {
        signoutUser();
        history.push('/signin');
      },
    );
  }, [
    openLoadingPanel,
    intl,
    fetchUser,
    closeLoadingPanel,
    determineRedirectPage,
    signoutUser,
    history,
  ]);

  const skipToEnd = useCallback(() => {
    setPage(THANK_YOU);
  }, [page]);

  const sendToDashboard = useCallback(() => {
    fetchUser(() => history.push('/dashboard'));
  }, [fetchUser, history]);

  // Navigation functions
  const nextSubPage = useCallback(() => {
    setSubPage(prev => prev + 1);
  }, []);

  const previousSubPage = useCallback(() => {
    setSubPage(prev => prev - 1);
  }, []);

  const nextPage = useCallback(() => {
    setPage(prev => prev + 1);
  }, []);

  const previousPage = useCallback(() => {
    setPage(prev => prev - 1);
  }, []);

  const updateFormDraft = useCallback((key, values) => {
    setFormDrafts(prev => ({
      ...prev,
      [key]: values,
    }));
  }, []);

  const draftChangeHandlers = useMemo(
    () => ({
      personalDetails: values => updateFormDraft('personalDetails', values),
      locationDetails: values => updateFormDraft('locationDetails', values),
      termsOfUse: values => updateFormDraft('termsOfUse', values),
      professionalDetails1: values =>
        updateFormDraft('professionalDetails1', values),
      professionalDetails2: values =>
        updateFormDraft('professionalDetails2', values),
      professionalDetails3: values =>
        updateFormDraft('professionalDetails3', values),
      professionalDetails4: values =>
        updateFormDraft('professionalDetails4', values),
      practiceDetails0: values => updateFormDraft('practiceDetails0', values),
      practiceDetails1: values => updateFormDraft('practiceDetails1', values),
      practiceDetails2: values => updateFormDraft('practiceDetails2', values),
      practiceDetails3: values => updateFormDraft('practiceDetails3', values),
      practiceDetails4: values => updateFormDraft('practiceDetails4', values),
      practiceDetails5: values => updateFormDraft('practiceDetails5', values),
      matchingPreferences: values =>
        updateFormDraft('matchingPreferences', values),
      taxInformation: values => updateFormDraft('taxInformation', values),
    }),
    [updateFormDraft],
  );

  // Component mount effect
  useEffect(() => {
    if (authenticated) {
      redirect();
    }
    fetchAgeGroups();
    fetchDesignations();
    fetchLanguages();
    fetchInsuranceCompanies();
    fetchApproachToCares();
    fetchTherapeuticOrientations();
    fetchPronouns();
    fetchDiagnosisTypes();
    fetchSpecialities();
    fetchReligions();
    fetchGenderIdentities();
    fetchRaces();
    fetchStates(COUNTRY === 'CA' ? 'CAN' : 'USA');
  }, [
    authenticated,
    redirect,
    fetchAgeGroups,
    fetchDesignations,
    fetchLanguages,
    fetchInsuranceCompanies,
    fetchApproachToCares,
    fetchTherapeuticOrientations,
    fetchPronouns,
    fetchDiagnosisTypes,
    fetchSpecialities,
    fetchReligions,
    fetchGenderIdentities,
    fetchRaces,
    fetchStates,
  ]);

  // Scroll to top when page or subPage changes
  useEffect(() => {
    scrollToTop();
  }, [page, subPage]);

  const pageProps = {
    currentStep: page,
    currentSubStep: subPage,
    nextPage,
    previousPage,
    openErrorDialog,
    openLoadingPanel,
    closeLoadingPanel,
    errorCallback,
    skipToEnd,
  };

  const firstSubPageProps = {
    ...pageProps,
    nextPage: nextSubPage,
  };

  const middleSubPageProps = {
    ...pageProps,
    nextPage: nextSubPage,
    previousPage: previousSubPage,
  };

  const endSubPageProps = {
    ...pageProps,
    nextPage: () => {
      nextPage();
      setSubPage(0);
    },
    previousPage: previousSubPage,
  };

  return (
    <>
      <NoIndex />
      <PreAuthTopBar locale={localStorage.getItem('locale')} />
      <div className="practitioner-onboarding-layout">
        <div className="light-background" />
        <div className="dark-background" />
        <Panel isClinicCounsellor={isClinicCounsellor} currentStep={page}>
          {page === INTRODUCTION && (
            <Introduction nonFormPage nextPage={nextPage} currentStep={page} />
          )}
          {page === PERSONAL_DETAILS && (
            <PersonalDetails
              {...pageProps}
              currentStep={page}
              draftValues={formDrafts.personalDetails}
              onDraftChange={draftChangeHandlers.personalDetails}
            />
          )}
          {page === LOCATION_DETAILS && (
            <LocationDetails
              {...pageProps}
              draftValues={formDrafts.locationDetails}
              onDraftChange={draftChangeHandlers.locationDetails}
            />
          )}
          {page === TERMS_OF_USE && (
            <TermsOfUse
              {...pageProps}
              draftValues={formDrafts.termsOfUse}
              onDraftChange={draftChangeHandlers.termsOfUse}
            />
          )}
          {page === PROFESSIONAL_DETAILS && subPage === 0 && (
            <ProfessionalDetails1
              {...firstSubPageProps}
              draftValues={formDrafts.professionalDetails1}
              onDraftChange={draftChangeHandlers.professionalDetails1}
            />
          )}
          {page === PROFESSIONAL_DETAILS && subPage === 1 && (
            <ProfessionalDetails2
              {...middleSubPageProps}
              designations={designations}
              draftValues={formDrafts.professionalDetails2}
              onDraftChange={draftChangeHandlers.professionalDetails2}
            />
          )}
          {page === PROFESSIONAL_DETAILS && subPage === 2 && (
            <ProfessionalDetails3
              {...middleSubPageProps}
              draftValues={formDrafts.professionalDetails3}
              onDraftChange={draftChangeHandlers.professionalDetails3}
              nextPage={() => {
                if (COUNTRY === 'US') {
                  setSubPage(3);
                } else {
                  nextPage();
                  setSubPage(0);
                }
              }}
            />
          )}
          {page === PROFESSIONAL_DETAILS && subPage === 3 && (
            <ProfessionalDetails4
              {...endSubPageProps}
              insuranceCompanies={insuranceCompanies}
              draftValues={formDrafts.professionalDetails4}
              onDraftChange={draftChangeHandlers.professionalDetails4}
              nextPage={() => {
                nextPage();
                if (COUNTRY === 'US') {
                  setSubPage(1);
                } else {
                  setSubPage(0);
                }
              }}
            />
          )}
          {page === PRACTICE_DETAILS && subPage === 0 && (
            <PracticeDetails0
              {...firstSubPageProps}
              languages={languages}
              setIsClinicCounsellor={setIsClinicCounsellor}
              draftValues={formDrafts.practiceDetails0}
              onDraftChange={draftChangeHandlers.practiceDetails0}
              previousPage={() => {
                previousPage();
                if (COUNTRY === 'US') {
                  setSubPage(3);
                } else {
                  setSubPage(2);
                }
              }}
            />
          )}
          {page === PRACTICE_DETAILS && subPage === 1 && (
            <PracticeDetails1
              {...middleSubPageProps}
              languages={languages}
              draftValues={formDrafts.practiceDetails1}
              onDraftChange={draftChangeHandlers.practiceDetails1}
            />
          )}
          {page === PRACTICE_DETAILS && subPage === 2 && (
            <PracticeDetails2
              {...middleSubPageProps}
              ageGroups={ageGroups}
              draftValues={formDrafts.practiceDetails2}
              onDraftChange={draftChangeHandlers.practiceDetails2}
            />
          )}
          {page === PRACTICE_DETAILS && subPage === 3 && (
            <PracticeDetails3
              {...middleSubPageProps}
              draftValues={formDrafts.practiceDetails3}
              onDraftChange={draftChangeHandlers.practiceDetails3}
            />
          )}
          {page === PRACTICE_DETAILS && subPage === 4 && (
            <PracticeDetails4
              {...middleSubPageProps}
              draftValues={formDrafts.practiceDetails4}
              onDraftChange={draftChangeHandlers.practiceDetails4}
            />
          )}
          {page === PRACTICE_DETAILS && subPage === 5 && (
            <PracticeDetails5
              {...endSubPageProps}
              ageGroups={ageGroups}
              draftValues={formDrafts.practiceDetails5}
              onDraftChange={draftChangeHandlers.practiceDetails5}
            />
          )}
          {page === MATCHING_PREFERENCES && (
            <MatchingPreferences
              {...pageProps}
              setPage={setPage}
              draftValues={formDrafts.matchingPreferences}
              onDraftChange={draftChangeHandlers.matchingPreferences}
              previousPage={() => {
                previousPage();
                setSubPage(5);
              }}
              skipToEnd={skipToEnd}
            />
          )}
          {page === TAX_INFORMATION && COUNTRY === 'CA' && (
            <TaxInformation
              {...pageProps}
              skipToEnd={skipToEnd}
              draftValues={formDrafts.taxInformation}
              onDraftChange={draftChangeHandlers.taxInformation}
            />
          )}
          {page === THANK_YOU && (
            <ThankYou
              currentStep={page}
              nextPage={sendToDashboard}
              nonFormPage
            />
          )}
          {loadingPanel.open && <LoadingPanel message={loadingPanel.message} />}
          <ErrorDialog
            open={error.open}
            handleClose={closeErrorDialog}
            message={error.message}
          />
        </Panel>
      </div>
    </>
  );
};

PracticeOnboarding.propTypes = {
  ageGroups: PropTypes.array,
  authenticated: PropTypes.bool,
  designations: PropTypes.array,
  fetchAgeGroups: PropTypes.func.isRequired,
  fetchApproachToCares: PropTypes.func.isRequired,
  fetchTherapeuticOrientations: PropTypes.func.isRequired,
  fetchDesignations: PropTypes.func.isRequired,
  fetchLanguages: PropTypes.func.isRequired,
  fetchPronouns: PropTypes.func.isRequired,
  fetchDiagnosisTypes: PropTypes.func.isRequired,
  fetchSpecialities: PropTypes.func.isRequired,
  fetchReligions: PropTypes.func.isRequired,
  fetchGenderIdentities: PropTypes.func.isRequired,
  fetchRaces: PropTypes.func.isRequired,
  fetchStates: PropTypes.func.isRequired,
  history: PropTypes.object.isRequired,
  intl: PropTypes.object.isRequired,
  languages: PropTypes.array,
  fetchUser: PropTypes.func.isRequired,
  fetchInsuranceCompanies: PropTypes.func.isRequired,
  insuranceCompanies: PropTypes.array,
  signoutUser: PropTypes.func.isRequired,
};

const mapStateToProps = state => ({
  ageGroups: state.misc.ageGroups,
  authenticated: state.auth.authenticated,
  designations: map(state.misc.designations, designation => ({
    value: designation.acronym,
    label: designation.name,
  })),
  languages: state.misc.languages,
  insuranceCompanies: state.misc.insuranceCompanies,
});

export default withLDConsumer()(
  injectIntl(
    connect(mapStateToProps, {
      fetchAgeGroups,
      fetchApproachToCares,
      fetchTherapeuticOrientations,
      fetchDesignations,
      fetchLanguages,
      fetchPronouns,
      fetchDiagnosisTypes,
      fetchSpecialities,
      fetchReligions,
      fetchGenderIdentities,
      fetchRaces,
      fetchStates,
      fetchUser,
      fetchInsuranceCompanies,
      signoutUser,
    })(PracticeOnboarding),
  ),
);
